/**
 * Redimensionează și comprimă poze mare (mobil / cameră) înainte de upload.
 * PDF nu este modificat. Ieșire raster: JPEG pentru dimensiuni mici predicte.
 */

const RASTER_TYPES = /^image\/(jpeg|jpg|png|webp|pjpeg|x-png)$/i;

function baseNameNoExt(filename) {
  const s = String(filename || "poza").replace(/\\/g, "/");
  const i = s.lastIndexOf("/");
  const leaf = i >= 0 ? s.slice(i + 1) : s;
  return leaf.replace(/\.[^/.]+$/, "") || "poza";
}

function rasterMimeOrExt(file) {
  const t = (file.type || "").toLowerCase();
  if (RASTER_TYPES.test(t)) return true;
  const leaf = String(file.name || "").toLowerCase();
  return /\.(jpe?g|png|webp)$/i.test(leaf);
}

function pdfFile(file) {
  const t = (file.type || "").toLowerCase();
  return t === "application/pdf" || /\.pdf$/i.test(String(file.name || ""));
}

function decodeDrawable(file) {
  return new Promise((resolve, reject) => {
    if (typeof createImageBitmap === "function") {
      createImageBitmap(file)
        .then(resolve)
        .catch(() => legacyImageDecode(file).then(resolve).catch(reject));
    } else {
      legacyImageDecode(file).then(resolve).catch(reject);
    }
  });
}

function legacyImageDecode(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("decode"));
    };
    img.src = url;
  });
}

function naturalSize(drawable) {
  if ("naturalWidth" in drawable && drawable.naturalWidth > 0) {
    return { w: drawable.naturalWidth, h: drawable.naturalHeight };
  }
  return { w: drawable.width, h: drawable.height };
}

async function drawableToJpegBlob(drawable, destW, destH, quality) {
  const canvas = document.createElement("canvas");
  canvas.width = destW;
  canvas.height = destH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("ctx");
  if (destW < 2 || destH < 2) throw new Error("dim");
  ctx.drawImage(drawable, 0, 0, destW, destH);

  const blob = await new Promise((res) =>
    canvas.toBlob((b) => res(b || null), "image/jpeg", quality),
  );

  return blob;
}

/**
 * Încearcă să aducă raster sub maxBytes (implicit 5 MB).
 * @param {File} file
 * @param {number} maxBytes
 * @returns {Promise<File>}
 */
export async function compressBookingImageForUpload(file, maxBytes) {
  if (!file || !(file instanceof File)) {
    return file;
  }

  if (pdfFile(file) || !rasterMimeOrExt(file)) {
    return file;
  }

  let drawable;
  try {
    drawable = await decodeDrawable(file);
  } catch {
    return file;
  }

  try {
    const { w: nw0, h: nh0 } = naturalSize(drawable);
    if (nw0 < 1 || nh0 < 1) {
      if ("close" in drawable && typeof drawable.close === "function") drawable.close();

      return file;
    }

    const maxEdges = [2560, 2048, 1920, 1600, 1280, 1024];
    const qualities = [0.88, 0.82, 0.74, 0.66, 0.56, 0.48];

    let bestBlob = null;
    const outName = `${baseNameNoExt(file.name)}.jpg`;

    for (const maxEdge of maxEdges) {
      const ratio = Math.min(1, maxEdge / Math.max(nw0, nh0));
      const dw = Math.max(1, Math.round(nw0 * ratio));
      const dh = Math.max(1, Math.round(nh0 * ratio));

      for (const q of qualities) {
        /** @type {Blob | null} */
        let blob = null;
        try {
          blob = await drawableToJpegBlob(drawable, dw, dh, q);
        } catch {
          blob = null;
        }
        if (!blob || blob.size < 1024) continue;
        bestBlob = blob;
        if (blob.size <= maxBytes) {
          try {
            if ("close" in drawable && typeof drawable.close === "function") drawable.close();
          } catch {
            //
          }

          return new File([blob], outName, { type: "image/jpeg", lastModified: Date.now() });
        }
      }
    }

    if (bestBlob) {
      try {
        if ("close" in drawable && typeof drawable.close === "function") drawable.close();
      } catch {
        //
      }

      return new File([bestBlob], outName, { type: "image/jpeg", lastModified: Date.now() });
    }
  } catch {
    // ignore
  }

  try {
    if ("close" in drawable && typeof drawable.close === "function") drawable.close();
  } catch {
    //
  }

  return file;
}

/** Pentru UX la selecție: imagini mari pot fi compactate la trimitere. */
export const BOOKING_ATTACHMENT_MAX_IMAGE_BEFORE_COMPRESS = 28 * 1024 * 1024;
